import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Search, X, Building2, User, Landmark, Network, BookOpen, Calendar, Activity, Beaker, ShieldCheck } from "lucide-react";
import SEO from "../components/SEO";

const TYPE_META = {
    person: { Icon: User, label: "Person", color: "text-[#94A3B8]" },
    company: { Icon: Building2, label: "Company", color: "text-[#56CCF2]" },
    organization: { Icon: Landmark, label: "Organisation", color: "text-[#2F80ED]" },
    // Research Explorer entity types (Phase 3.11)
    disease: { Icon: Activity, label: "Disease", color: "text-[#F87171]" },
    manufacturer: { Icon: Building2, label: "Manufacturer", color: "text-[#56CCF2]" },
    ingredient: { Icon: Beaker, label: "Ingredient", color: "text-[#FBBF24]" },
    surveillance_system: { Icon: ShieldCheck, label: "Surveillance", color: "text-[#34D399]" },
};

const TYPES = ["person", "company", "organization", "disease", "manufacturer", "ingredient", "surveillance_system"];

export default function EntitiesIndexPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [entities, setEntities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [typeFilter, setTypeFilterState] = useState(searchParams.get("type") || "");

    // Keep URL ?type= in sync so the homepage stat tiles deep-link correctly
    // and the back/forward buttons restore filter state.
    const setTypeFilter = (t) => {
        setTypeFilterState(t);
        const next = new URLSearchParams(searchParams);
        if (t) next.set("type", t); else next.delete("type");
        setSearchParams(next, { replace: true });
    };

    useEffect(() => {
        setLoading(true);
        api.get("/entities").then((r) => setEntities(r.data || [])).catch(() => {}).finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        let items = entities;
        if (typeFilter) items = items.filter((e) => (e.type || "").toLowerCase() === typeFilter);
        if (q.trim()) {
            const ql = q.trim().toLowerCase();
            items = items.filter((e) => e.name.toLowerCase().includes(ql) || e.slug.includes(ql));
        }
        return items;
    }, [entities, q, typeFilter]);

    return (
        <div className="min-h-screen bg-[#0B1220]">
            <SEO
                title="Entity Index"
                description="Every person, company, and institution referenced across the library — normalised, indexed, cross-linked. People, companies, regulators, NGOs, intergovernmental bodies."
                canonicalPath="/entities"
            />
            <Header />

            {/* Hero */}
            <section className="relative overflow-hidden border-b border-[#243044]">
                <div className="absolute inset-0 bg-grid opacity-30" />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0B1220]/40 via-[#0B1220]/85 to-[#0B1220]" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12 sm:pt-24 sm:pb-14">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#111827] border border-[#243044] mb-8">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#56CCF2] animate-pulse" />
                            <span className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] font-medium">
                                Entity index
                            </span>
                        </div>
                        <h1 data-testid="entities-heading" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05] mb-6">
                            People, companies<br />& institutions.
                        </h1>
                        <p className="text-lg text-[#94A3B8] leading-relaxed max-w-2xl">
                            Every entity referenced across the library, normalised and indexed. Open any node to see the investigations, timeline events, sources and co-occurring entities it touches — a living systems map.
                        </p>
                        <div className="flex flex-wrap gap-4 mt-8 text-xs text-[#64748B]">
                            <span data-testid="entities-total"><span className="text-white font-bold">{entities.length}</span> canonical entities</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Filters */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
                <div className="bg-[#111827] border border-[#243044] rounded-2xl p-5 space-y-4">
                    <div className="flex items-center gap-3 bg-[#0B1220] border border-[#243044] rounded-xl px-4 py-3 focus-within:border-[#2F80ED]">
                        <Search className="w-4 h-4 text-[#64748B] shrink-0" />
                        <input
                            data-testid="entities-search-input"
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search entities — BlackRock, WHO, FDA, BBC, Pfizer…"
                            className="bg-transparent border-0 outline-none text-sm text-white placeholder-[#64748B] flex-1"
                        />
                        {q && <button onClick={() => setQ("")} className="text-[#64748B] hover:text-white"><X className="w-4 h-4" /></button>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            data-testid="entities-type-all"
                            onClick={() => setTypeFilter("")}
                            className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider font-medium transition-colors border ${!typeFilter ? "bg-[#2F80ED] text-white border-[#2F80ED]" : "bg-[#0B1220] text-[#94A3B8] border-[#243044] hover:border-[#2F80ED]"}`}
                        >
                            All
                        </button>
                        {TYPES.map((t) => (
                            <button
                                key={t}
                                data-testid={`entities-type-${t}`}
                                onClick={() => setTypeFilter(t)}
                                className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider font-medium transition-colors border ${typeFilter === t ? "bg-[#2F80ED] text-white border-[#2F80ED]" : "bg-[#0B1220] text-[#94A3B8] border-[#243044] hover:border-[#2F80ED]"}`}
                            >
                                {TYPE_META[t]?.label || t}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Grid */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                <div className="flex items-baseline justify-between mb-6">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] font-medium">
                        Canonical entities
                    </div>
                    <div data-testid="entities-result-count" className="text-xs text-[#64748B]">
                        {loading ? "Loading…" : `${filtered.length} entit${filtered.length === 1 ? "y" : "ies"}`}
                    </div>
                </div>

                {loading ? (
                    <div className="text-center text-sm text-[#64748B] py-20">Loading entity index…</div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-20 text-sm text-[#CBD5E1]">
                        No entities match your filters. <button onClick={() => { setQ(""); setTypeFilter(""); }} className="text-[#56CCF2] hover:text-white">Clear</button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filtered.map((e) => {
                            const meta = TYPE_META[(e.type || "").toLowerCase()] || TYPE_META.organization;
                            const Icon = meta.Icon;
                            return (
                                <Link
                                    key={e.slug}
                                    to={`/entity/${e.slug}`}
                                    data-testid={`entity-card-${e.slug}`}
                                    className="group block bg-[#111827] border border-[#243044] rounded-xl p-5 hover:border-[#2F80ED] transition-all duration-300 hover:-translate-y-0.5"
                                >
                                    <div className="flex items-start gap-3 mb-3">
                                        <div className={`w-10 h-10 rounded-lg bg-[#0B1220] border border-[#243044] flex items-center justify-center ${meta.color}`}>
                                            <Icon className="w-4 h-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-0.5">{meta.label}</p>
                                            <h3 className="text-base font-semibold text-white leading-snug group-hover:text-[#56CCF2] transition-colors truncate">
                                                {e.name}
                                            </h3>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] pt-3 border-t border-[#243044]">
                                        <span className="inline-flex items-center gap-1.5"><Network className="w-3 h-3" />{e.topic_count} topic{e.topic_count === 1 ? "" : "s"}</span>
                                        <span className="inline-flex items-center gap-1.5"><Calendar className="w-3 h-3" />{e.event_count} events</span>
                                        <span className="inline-flex items-center gap-1.5 ml-auto"><BookOpen className="w-3 h-3" />{e.source_count}</span>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                )}
            </section>

            <Footer />
        </div>
    );
}
